import { visibleProductDetailName } from './product-display';

export interface OutboundPrintData {
  company: {
    name: string;
    address: string;
    taxCode: string;
    bankAccountName: string;
    bankAccountNumber: string;
    bankName: string;
  };
  invoice: {
    id: string;
    sku: string;
    date: string;
    deliveryDate?: string | null;
    total: number;
    vat: number;
    note: string;
    customerName: string;
    customerAddress: string;
    customerPhone: string;
    paymentCondition: string;
    creatorName: string;
    shipperName: string;
    saleName: string;
  };
  lines: Array<{
    productName: string;
    detailName: string;
    unitName: string;
    warehouseCode: string;
    quantity: number;
    price: number;
    discountPercent: number;
    totalBeforeDiscount: number;
    totalAfterDiscount: number;
    isGift: boolean;
  }>;
  printedAt: string;
}

const number = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 });
const printedAtFormatter = new Intl.DateTimeFormat('vi-VN', {
  timeZone: 'Asia/Ho_Chi_Minh',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

function invoiceDate(value: string): string {
  const [year, month, day] = value.slice(0, 10).split('-');
  return [day, month, year].filter(Boolean).join('/');
}

function printTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(printedAtFormatter.formatToParts(date).map(({ type, value: part }) => [type, part]));
  return `${parts.hour}:${parts.minute}:${parts.second} ${parts.day}/${parts.month}/${parts.year}`;
}

export function OutboundPrintSheet({ data }: { data: OutboundPrintData }) {
  const { company, invoice, lines } = data;
  const bankQr = company.bankName && company.bankAccountNumber
    ? `https://img.vietqr.io/image/${encodeURIComponent(company.bankName)}-${encodeURIComponent(company.bankAccountNumber)}-compact.png`
    : '';

  return (
    <article className="outbound-print-sheet" aria-label={`Hóa đơn bán hàng ${invoice.sku}`}>
      <div className="print-generated-at">{printTimestamp(data.printedAt)}</div>
      <header className="print-company">
        <div className="print-company-identity">
          <h2>{company.name}</h2>
          {company.address ? <p><b>ĐỊA CHỈ:</b> {company.address}</p> : null}
          {company.taxCode ? <p><b>MÃ SỐ THUẾ:</b> {company.taxCode}</p> : null}
        </div>
        {company.bankAccountName || company.bankAccountNumber || company.bankName ? (
          <div className="print-bank">
            <div>
              <h3>THÔNG TIN TÀI KHOẢN NGÂN HÀNG</h3>
              {company.bankAccountName ? <p><b>TÀI KHOẢN:</b> {company.bankAccountName}</p> : null}
              {company.bankAccountNumber ? <p><b>SỐ TÀI KHOẢN:</b> {company.bankAccountNumber}</p> : null}
              {company.bankName ? <p><b>NGÂN HÀNG:</b> {company.bankName}</p> : null}
            </div>
            {bankQr ? <img src={bankQr} alt="Mã QR chuyển khoản" /> : null}
          </div>
        ) : null}
      </header>

      <h1>HÓA ĐƠN BÁN HÀNG</h1>
      <section className="print-contacts">
        <div>
          <p><b>KHÁCH HÀNG:</b> {invoice.customerName || '—'}</p>
          {invoice.customerAddress ? <p><b>ĐỊA CHỈ:</b> {invoice.customerAddress}</p> : null}
          {invoice.customerPhone ? <p><b>SỐ ĐIỆN THOẠI:</b> {invoice.customerPhone}</p> : null}
          {invoice.paymentCondition ? <p className="print-payment-condition"><b>ĐKTT:</b> {invoice.paymentCondition}</p> : null}
        </div>
        <div className="print-invoice-meta">
          <strong>{invoice.sku}</strong>
          {invoice.creatorName ? <p>Nhân viên xuất: {invoice.creatorName}</p> : null}
          {invoice.shipperName ? <p>Nhân viên giao: {invoice.shipperName}</p> : null}
          {invoice.saleName ? <p>Nhân viên sale: {invoice.saleName}</p> : null}
          <p>Ngày xuất: {invoiceDate(invoice.date)}</p>
          {invoice.deliveryDate ? <p>Ngày giao dự kiến: {invoiceDate(invoice.deliveryDate)}</p> : null}
        </div>
      </section>

      <table className="print-product-table">
        <colgroup>
          <col style={{ width: '4%' }} /><col style={{ width: '36%' }} /><col style={{ width: '7%' }} />
          <col style={{ width: '5%' }} /><col style={{ width: '10%' }} /><col style={{ width: '14%' }} />
          <col style={{ width: '10%' }} /><col style={{ width: '14%' }} />
        </colgroup>
        <thead><tr><th>STT</th><th>SẢN PHẨM</th><th>ĐVT</th><th>SL</th><th>ĐƠN<br />GIÁ</th><th>THÀNH TIỀN<br />TRƯỚC CK</th><th>CHIẾT KHẤU<br />(%)</th><th>THÀNH TIỀN<br />SAU CK</th></tr></thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={`${index}:${line.productName}:${line.warehouseCode}`}>
              <td>{index + 1}</td>
              <td>{line.productName}{line.isGift ? ' (Quà tặng)' : ''}{visibleProductDetailName(line.productName, line.detailName) ? <small> - {line.detailName}</small> : null}</td>
              <td className="print-centered">{line.unitName}</td>
              <td className="print-centered">{number.format(line.quantity)}</td>
              <td className="print-number">{number.format(line.price)}</td>
              <td className="print-number">{number.format(line.totalBeforeDiscount)}</td>
              <td className="print-centered">{number.format(line.discountPercent)}</td>
              <td className="print-number">{number.format(line.totalAfterDiscount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <table className="print-totals-table">
        <colgroup><col style={{ width: '86%' }} /><col style={{ width: '14%' }} /></colgroup>
        <tbody>
          <tr><td>THUẾ (%)</td><td>{number.format(invoice.vat)}</td></tr>
          <tr><td>TỔNG CỘNG</td><td>{number.format(invoice.total)}</td></tr>
        </tbody>
      </table>

      {invoice.note ? <p className="print-note"><b>GHI CHÚ:</b> {invoice.note}</p> : null}
      <footer className="print-signatures">
        {['Khách hàng', 'Người giao hàng', 'Thủ kho', 'Nhân viên kế toán'].map((label) => <div key={label}><b>{label}</b><i>(ký và ghi rõ họ tên)</i></div>)}
      </footer>
    </article>
  );
}
